import {AsyncResult} from 'Core/models/Async';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import I from 'immutable';
import {all, call, put, select, takeLatest} from 'redux-saga/effects';
import {ReducerState} from 'rootReducer';
import {AgentNumberWithStatusDto} from 'Users/api';
import agentNumberApi from 'Users/api/agentNumberApi';
import {
  matchTaxIdentificationNumber,
  TaxIdentificationNumberWithNoAgentNumbers,
  ValidatingTaxIdentificationNumber,
  ValidTaxIdentificationNumber,
} from 'Users/models/User/Agent/TaxIdentificationNumber';
import {matchAgentUserType} from 'Users/models/User/AgentUserType';
import {matchUserTypeByLineOfBusiness, UserType} from 'Users/models/User/UserType';
import {changeTaxId, AgentSupportUserTypeActionType, loadTaxId} from 'Users/ducks/agentSupportUserType';
import {SupportAgencyAssociation} from 'Users/models/User/Agent/SupportAgencyAssociation';
import {AvailableAgentNumber} from 'Users/components/UserAdministration/UserEntry/AgentNumberInput';

function* loadAgentNumbersSaga(validatingTaxIdNumber: ValidatingTaxIdentificationNumber, index: number) {
  const agentNumbers: AsyncResult<AgentNumberWithStatusDto[]> = yield call(
    agentNumberApi.getAgentNumbersByTaxIdentifier,
    validatingTaxIdNumber.value
  );

  yield pipe(
    agentNumbers,
    O.fromEither,
    O.chain(NEA.fromArray),
    O.fold(
      () => put(loadTaxId(index, new TaxIdentificationNumberWithNoAgentNumbers(validatingTaxIdNumber))),
      agentNumbers =>
        put(
          loadTaxId(
            index,
            new ValidTaxIdentificationNumber(
              validatingTaxIdNumber,
              agentNumbers.map(
                (an): AvailableAgentNumber => ({
                  isNewYork: an.isNewYork!,
                  value: an.value!,
                  status: {id: an.status!.id!, name: an.status!.name!},
                })
              )
            )
          )
        )
    )
  );
}

function* validateTaxIdSaga() {
  yield takeLatest(AgentSupportUserTypeActionType.CHANGE_TAX_ID, function*(action: ReturnType<typeof changeTaxId>) {
    const {index} = action.payload;
    const userTypes: I.List<UserType> = yield select((state: ReducerState) => state.user.userEntry.userTypes);

    const agentUserType = userTypes
      .filter(ut =>
        matchUserTypeByLineOfBusiness(ut.value, {
          Agent: () => true,
          Default: () => false,
        })
      )
      .map(ut =>
        matchUserTypeByLineOfBusiness(ut.value, {
          Agent: agent => agent,
          Default: () => undefined,
        })
      )
      .first(undefined);

    if (agentUserType) {
      const agencyAssociation = matchAgentUserType<SupportAgencyAssociation | undefined>(agentUserType.value, {
        Agent: () => undefined,
        AgentSupport: agentSupport => agentSupport.agencyAssociations.get(index),
        VIP: () => undefined,
        IAM: () => undefined,
        Undetermined: () => undefined,
      });

      if (agencyAssociation) {
        yield matchTaxIdentificationNumber(agencyAssociation.taxId, {
          ValidatingTaxIdentificationNumber: validatingTaxIdNumber =>
            loadAgentNumbersSaga(validatingTaxIdNumber, index),
          Default: () => {},
        });
      }
    }
  });
}

export default function*() {
  return yield all([validateTaxIdSaga()]);
}
